/**
 * @description Frontend API service for recurring payment plans.
 *
 * Key features:
 * - CRUD for recurring plans
 * - Plan lifecycle actions (pause, resume, cancel)
 * - Statistics and upcoming charge date preview (isFirstChargeToday)
 *
 * @dependencies
 * - apiClient: Axios instance with auth headers and company context
 * @notes preview/create send viewerToday (browser YYYY-MM-DD) so Charge $
 *   matches the merchant's local today. Cron remains company-local.
 */

import apiClient from './apiClient';

const recurringPlanService = {
  /**
   * Get paginated list of recurring plans.
   * @param {object} params - { status, customerId, search, page, limit }
   * @returns {Promise<{ plans, totalCount, page, totalPages }>}
   */
  getPlans: async (params = {}) => {
    const response = await apiClient.get('/recurring-plans', { params });
    return response.data;
  },

  /**
   * Get statistics for recurring plans.
   * @returns {Promise<{ statistics }>}
   */
  getStatistics: async () => {
    const response = await apiClient.get('/recurring-plans/statistics');
    return response.data;
  },

  /**
   * Get a single plan with charge history.
   * @param {string} planId - UUID
   * @returns {Promise<{ plan }>}
   */
  getPlan: async (planId) => {
    const response = await apiClient.get(`/recurring-plans/${planId}`);
    return response.data;
  },

  /**
   * Get upcoming charge dates for a plan.
   * @param {string} planId - UUID
   * @param {number} count - Number of dates (default 5)
   * @returns {Promise<{ dates: string[] }>}
   */
  getUpcomingDates: async (planId, count = 5) => {
    const response = await apiClient.get(`/recurring-plans/${planId}/upcoming-dates`, {
      params: { count }
    });
    return response.data;
  },

  /**
   * Get charge history for a plan.
   * @param {string} planId - UUID
   * @param {object} params - { page, limit }
   * @returns {Promise<{ chargeHistory, totalCount, page, totalPages }>}
   */
  getChargeHistory: async (planId, params = {}) => {
    const response = await apiClient.get(`/recurring-plans/${planId}/charge-history`, { params });
    return response.data;
  },

  /**
   * Preview a recurring plan schedule without saving.
   * Returns the computed first charge date plus the next several upcoming charges,
   * so the create/edit modal can show "Customer will be billed on the [6th]…".
   * @param {object} input - { startDate, frequency, intervalCount, endDate, maxOccurrences, firstChargeBehavior, viewerToday }
   * @returns {Promise<{ firstChargeDate, isFirstChargeToday, dayOfMonth, dayOfWeek, isMonthEndAnchor, upcomingCharges }>}
   */
  previewSchedule: async (input) => {
    const response = await apiClient.post('/recurring-plans/preview', input);
    return response.data;
  },

  /**
   * Create a new recurring plan.
   * @param {object} planData
   * @returns {Promise<{ plan }>}
   */
  createPlan: async (planData) => {
    const response = await apiClient.post('/recurring-plans', planData);
    return response.data;
  },

  /**
   * Update a recurring plan.
   * @param {string} planId - UUID
   * @param {object} updates
   * @returns {Promise<{ plan }>}
   */
  updatePlan: async (planId, updates) => {
    const response = await apiClient.put(`/recurring-plans/${planId}`, updates);
    return response.data;
  },

  /**
   * Pause a recurring plan.
   * @param {string} planId - UUID
   * @returns {Promise<{ plan }>}
   */
  pausePlan: async (planId) => {
    const response = await apiClient.post(`/recurring-plans/${planId}/pause`);
    return response.data;
  },

  /**
   * Resume a paused plan.
   * @param {string} planId - UUID
   * @param {object} [options]
   * @param {string} [options.mode='continue'] - continue | bill_missed
   * @returns {Promise<{ plan }>}
   */
  resumePlan: async (planId, options = {}) => {
    const body = {};
    if (options.mode !== undefined) body.mode = options.mode;
    const response = await apiClient.post(`/recurring-plans/${planId}/resume`, body);
    return response.data;
  },

  /**
   * Cancel a recurring plan.
   * @param {string} planId - UUID
   * @returns {Promise<{ plan }>}
   */
  cancelPlan: async (planId) => {
    const response = await apiClient.post(`/recurring-plans/${planId}/cancel`);
    return response.data;
  }
};

export default recurringPlanService;
